<template>
  <div class="container mx-auto mt-16 md:mt-20 lg:mt-32 xl:mt-40">
    <h2 class="max-w-6xl text-3xl md:text-4xl lg:text-5xl font-semibold">
      {{ dataLocale.any_content.title }}
    </h2>
    <div
      class="grid grid-cols-1 sm:grid-cols-2 mt-12 lg:mt-20 lg:grid-cols-3 gap-x-8 sm:gap-x-12 gap-y-12 lg:gap-y-22"
    >
      <div
        class="grid lg:justify-items-center p-4 border-2 border-neutral-200 rounded-lg"
        v-for="reason in dataLocale.data"
      >
        <div
          class="bg-success-soft flex items-center justify-center w-20 h-20 place-content-center rounded-2xl"
        >
          <component v-bind:is="reason.icon" class="w-16 h-16 text-success" />
        </div>
        <h6
          class="mt-4 mb-4 lg:text-center text-xl font-semibold"
          :class="{ 'font-inter': locale == 'vi' }"
        >
          {{ reason.title }}
        </h6>
        <p class="max-w-sm sm:max-w-md lg:text-center">
          {{ reason.description }}
        </p>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import BagIcon from "~/assets/svg/marketing/bag.svg?component";
import BrandingIcon from "~/assets/svg/marketing/branding.svg?component";
import CardIcon from "~/assets/svg/marketing/card.svg?component";
import ConnectIcon from "~/assets/svg/marketing/connect.svg?component";
import ConversionIcon from "~/assets/svg/marketing/conversion.svg?component";
import PrintIcon from "~/assets/svg/marketing/print.svg?component";
import TrendingIcon from "~/assets/svg/marketing/trending.svg?component";
import MultiDataLocale from "~~/config/interface/MultiDataLocale";
export default defineComponent({
  components: {
    BagIcon,
    BrandingIcon,
    CardIcon,
    ConnectIcon,
    ConversionIcon,
    PrintIcon,
    TrendingIcon,
  },
  setup() {
    const dataLocale = new MultiDataLocale([
      {
        locale: "en",
        title: "Why QR Code Marketing is a Smart Choice",
        data: [
          {
            icon: "TrendingIcon",
            title: "Stay Ahead with Mobile Marketing Trends",
            description:
              "Mobile marketing is undeniably in vogue. Harnessing its potential to offer customers seamless access to information, exclusive discounts, and promotions will position you ahead of your competitors.",
          },
          {
            icon: "BagIcon",
            title: "Understand Customer Behavior for Better Marketing",
            description:
              "By integrating tracking solutions like QR codes, you can unlock valuable insights into customer preferences, allowing you to fine-tune your marketing strategies to meet their needs.",
          },
          {
            icon: "PrintIcon",
            title: "Expand Your Reach with Printed Media",
            description:
              "Incorporate QR codes into your print assets—brochures, flyers, and business cards—to provide customers with immediate access to your digital content and promotions, bridging the gap between offline and online experiences.",
          },
          {
            icon: "CardIcon",
            title: "Save Time and Money with Effortless Marketing Tools",
            description:
              "QR codes are an affordable, easily generated marketing tool that can be swiftly produced and distributed, ensuring significant savings in both time and budget for your promotional campaigns.",
          },
          {
            icon: "ConversionIcon",
            title:
              "Enhance Marketing Effectiveness through Streamlined Customer Journeys",
            description:
              "QR codes simplify the customer experience, making it easier to attract and retain your audience. By offering direct access to promotions and information, you significantly boost the efficiency of your marketing tactics.",
          },
          {
            icon: "BrandingIcon",
            title: "Elevate Brand Recognition with Personalized QR Codes",
            description:
              "QR codes can be tailored with your unique brand logo and color scheme, strengthening your brand identity and fostering greater awareness among your target demographic.",
          },
        ],
      },
      {
        locale: "fr",
        title: "Pourquoi le marketing par code QR est un choix judicieux",
        data: [
          {
            icon: "TrendingIcon",
            title:
              "Gardez une longueur d'avance grâce aux tendances du marketing mobile",
            description:
              "Le marketing mobile est indéniablement en vogue. Exploiter son potentiel pour offrir aux clients un accès fluide aux informations, aux réductions exclusives et aux promotions vous positionnera en tête de vos concurrents.",
          },
          {
            icon: "BagIcon",
            title:
              "Comprendre le comportement des clients pour un meilleur marketing",
            description:
              "En intégrant des solutions de suivi telles que les codes QR, vous pouvez débloquer des informations précieuses sur les préférences des clients, vous permettant ainsi d'affiner vos stratégies marketing pour répondre à leurs besoins.",
          },
          {
            icon: "PrintIcon",
            title: "Élargissez votre portée avec les supports imprimés",
            description:
              "Incorporez des codes QR dans vos supports imprimés (brochures, flyers et cartes de visite) pour offrir aux clients un accès immédiat à votre contenu numérique et à vos promotions, comblant ainsi le fossé entre les expériences hors ligne et en ligne.",
          },
          {
            icon: "CardIcon",
            title:
              "Gagnez du temps et de l'argent grâce à des outils marketing faciles à utiliser",
            description:
              "Les codes QR sont un outil marketing abordable et facile à générer qui peut être produit et distribué rapidement, garantissant des économies importantes en temps et en budget pour vos campagnes promotionnelles.",
          },
          {
            icon: "ConversionIcon",
            title:
              "Améliorez l'efficacité du marketing grâce à des parcours client rationalisés",
            description:
              "Les codes QR simplifient l'expérience client, ce qui facilite l'attraction et la fidélisation de votre public. En offrant un accès direct aux promotions et aux informations, vous augmentez considérablement l'efficacité de vos tactiques marketing.",
          },
          {
            icon: "BrandingIcon",
            title:
              "Rehaussez la reconnaissance de la marque grâce à des codes QR personnalisés",
            description:
              "Les codes QR peuvent être personnalisés avec votre logo de marque unique et votre palette de couleurs, renforçant ainsi l'identité de votre marque et favorisant une plus grande sensibilisation auprès de votre public cible.",
          },
        ],
      },
      {
        locale: "it",
        title: "Perché il marketing con codice QR è una scelta intelligente",
        data: [
          {
            icon: "TrendingIcon",
            title: "Rimani al passo con le tendenze del marketing mobile",
            description:
              "Il marketing mobile è innegabilmente di moda. Sfruttare il suo potenziale per offrire ai clienti un accesso senza problemi a informazioni, sconti esclusivi e promozioni ti posizionerà davanti ai tuoi concorrenti.",
          },
          {
            icon: "BagIcon",
            title:
              "Comprendi il comportamento dei clienti per un marketing migliore",
            description:
              "Integrando soluzioni di tracciamento come i codici QR, puoi sbloccare preziose informazioni sulle preferenze dei clienti, consentendoti di mettere a punto le tue strategie di marketing per soddisfare le loro esigenze.",
          },
          {
            icon: "PrintIcon",
            title: "Espandi la tua portata con i supporti cartacei",
            description:
              "Incorpora i codici QR nei tuoi materiali stampati (brochure, volantini e biglietti da visita) per fornire ai clienti un accesso immediato ai tuoi contenuti digitali e alle promozioni, colmando il divario tra esperienze offline e online.",
          },
          {
            icon: "CardIcon",
            title:
              "Risparmia tempo e denaro con strumenti di marketing semplici",
            description:
              "I codici QR sono uno strumento di marketing economico e facile da generare che può essere prodotto e distribuito rapidamente, garantendo un notevole risparmio di tempo e budget per le tue campagne promozionali.",
          },
          {
            icon: "ConversionIcon",
            title:
              "Migliora l'efficacia del marketing attraverso percorsi cliente semplificati",
            description:
              "I codici QR semplificano l'esperienza del cliente, rendendo più facile attrarre e fidelizzare il tuo pubblico. Offrendo un accesso diretto a promozioni e informazioni, aumenti significativamente l'efficienza delle tue tattiche di marketing.",
          },
          {
            icon: "BrandingIcon",
            title:
              "Aumenta il riconoscimento del marchio con codici QR personalizzati",
            description:
              "I codici QR possono essere personalizzati con il logo del tuo marchio unico e la combinazione di colori, rafforzando l'identità del tuo marchio e promuovendo una maggiore consapevolezza tra il tuo target demografico.",
          },
        ],
      },
      {
        locale: "de",
        title: "Warum QR-Code-Marketing eine kluge Wahl ist",
        data: [
          {
            icon: "TrendingIcon",
            title:
              "Bleiben Sie mit den Trends im mobilen Marketing auf dem Laufenden",
            description:
              "Mobiles Marketing ist unbestreitbar angesagt. Die Nutzung seines Potenzials, Kunden einen nahtlosen Zugang zu Informationen, exklusiven Rabatten und Werbeaktionen zu bieten, wird Sie Ihren Mitbewerbern einen Schritt voraus sein lassen.",
          },
          {
            icon: "BagIcon",
            title:
              "Verstehen Sie das Kundenverhalten für ein besseres Marketing",
            description:
              "Durch die Integration von Tracking-Lösungen wie QR-Codes können Sie wertvolle Einblicke in die Präferenzen der Kunden gewinnen und so Ihre Marketingstrategien an deren Bedürfnisse anpassen.",
          },
          {
            icon: "PrintIcon",
            title: "Erweitern Sie Ihre Reichweite mit Printmedien",
            description:
              "Integrieren Sie QR-Codes in Ihre Printmaterialien – Broschüren, Flyer und Visitenkarten –, um Kunden einen sofortigen Zugriff auf Ihre digitalen Inhalte und Werbeaktionen zu ermöglichen und die Lücke zwischen Offline- und Online-Erlebnissen zu schließen.",
          },
          {
            icon: "CardIcon",
            title: "Sparen Sie Zeit und Geld mit mühelosen Marketing-Tools",
            description:
              "QR-Codes sind ein kostengünstiges, einfach zu generierendes Marketing-Tool, das schnell erstellt und verteilt werden kann und so erhebliche Einsparungen bei Zeit und Budget für Ihre Werbekampagnen gewährleistet.",
          },
          {
            icon: "ConversionIcon",
            title:
              "Steigern Sie die Marketingeffektivität durch optimierte Kundenreisen",
            description:
              "QR-Codes vereinfachen das Kundenerlebnis und erleichtern es, Ihr Publikum anzuziehen und zu binden. Durch den direkten Zugriff auf Werbeaktionen und Informationen steigern Sie die Effizienz Ihrer Marketingtaktiken erheblich.",
          },
          {
            icon: "BrandingIcon",
            title:
              "Steigern Sie die Markenbekanntheit mit personalisierten QR-Codes",
            description:
              "QR-Codes können mit Ihrem einzigartigen Markenlogo und Farbschema angepasst werden, wodurch Ihre Markenidentität gestärkt und das Bewusstsein bei Ihrer Zielgruppe gefördert wird.",
          },
        ],
      },
      {
        locale: "es",
        title:
          "Por qué el marketing con códigos QR es una elección inteligente",
        data: [
          {
            icon: "TrendingIcon",
            title:
              "Manténgase a la vanguardia con las tendencias del marketing móvil",
            description:
              "El marketing móvil está innegablemente de moda. Aprovechar su potencial para ofrecer a los clientes un acceso fluido a información, descuentos exclusivos y promociones lo posicionará por delante de sus competidores.",
          },
          {
            icon: "BagIcon",
            title:
              "Comprenda el comportamiento del cliente para un mejor marketing",
            description:
              "Al integrar soluciones de seguimiento como los códigos QR, puede desbloquear información valiosa sobre las preferencias de los clientes, lo que le permite ajustar sus estrategias de marketing para satisfacer sus necesidades.",
          },
          {
            icon: "PrintIcon",
            title: "Amplíe su alcance con los medios impresos",
            description:
              "Incorpore códigos QR en sus materiales impresos (folletos, volantes y tarjetas de presentación) para proporcionar a los clientes acceso inmediato a su contenido digital y promociones, cerrando la brecha entre las experiencias fuera de línea y en línea.",
          },
          {
            icon: "CardIcon",
            title:
              "Ahorre tiempo y dinero con herramientas de marketing sencillas",
            description:
              "Los códigos QR son una herramienta de marketing asequible y fácil de generar que se puede producir y distribuir rápidamente, lo que garantiza un ahorro significativo tanto en tiempo como en presupuesto para sus campañas promocionales.",
          },
          {
            icon: "ConversionIcon",
            title:
              "Mejore la eficacia del marketing a través de recorridos de clientes optimizados",
            description:
              "Los códigos QR simplifican la experiencia del cliente, lo que facilita la atracción y retención de su público. Al ofrecer acceso directo a promociones e información, aumenta significativamente la eficiencia de sus tácticas de marketing.",
          },
          {
            icon: "BrandingIcon",
            title:
              "Eleve el reconocimiento de la marca con códigos QR personalizados",
            description:
              "Los códigos QR se pueden personalizar con el logotipo y el esquema de color únicos de su marca, fortaleciendo su identidad de marca y fomentando una mayor conciencia entre su grupo demográfico objetivo.",
          },
        ],
      },
      {
        locale: "nl",
        title: "Waarom QR-code marketing een slimme keuze is",
        data: [
          {
            icon: "TrendingIcon",
            title: "Blijf voorop met trends in mobiele marketing",
            description:
              "Mobiele marketing is onmiskenbaar in de mode. Het benutten van het potentieel om klanten naadloze toegang te bieden tot informatie, exclusieve kortingen en promoties zal u een voorsprong geven op uw concurrenten.",
          },
          {
            icon: "BagIcon",
            title: "Begrijp klantgedrag voor betere marketing",
            description:
              "Door trackingoplossingen zoals QR-codes te integreren, kunt u waardevolle inzichten ontgrendelen in klantvoorkeuren, waardoor u uw marketingstrategieën kunt afstemmen op hun behoeften.",
          },
          {
            icon: "PrintIcon",
            title: "Vergroot uw bereik met gedrukte media",
            description:
              "Integreer QR-codes in uw gedrukte materialen - brochures, flyers en visitekaartjes - om klanten onmiddellijk toegang te geven tot uw digitale content en promoties, waardoor de kloof tussen offline en online ervaringen wordt overbrugd.",
          },
          {
            icon: "CardIcon",
            title: "Bespaar tijd en geld met moeiteloze marketingtools",
            description:
              "QR-codes zijn een betaalbare, eenvoudig te genereren marketingtool die snel kan worden geproduceerd en verspreid, waardoor aanzienlijke besparingen in zowel tijd als budget voor uw promotiecampagnes worden gegarandeerd.",
          },
          {
            icon: "ConversionIcon",
            title:
              "Verbeter de marketingeffectiviteit via gestroomlijnde klantreizen",
            description:
              "QR-codes vereenvoudigen de klantervaring, waardoor het gemakkelijker wordt om uw publiek aan te trekken en te behouden. Door directe toegang tot promoties en informatie te bieden, verhoogt u de efficiëntie van uw marketingtactieken aanzienlijk.",
          },
          {
            icon: "BrandingIcon",
            title: "Vergroot merkherkenning met gepersonaliseerde QR-codes",
            description:
              "QR-codes kunnen worden aangepast met uw unieke merklogo en kleurenschema, waardoor uw merkidentiteit wordt versterkt en het bewustzijn onder uw doelgroep wordt vergroot.",
          },
        ],
      },
      {
        locale: "pt",
        title: "Por que o marketing com código QR é uma escolha inteligente",
        data: [
          {
            icon: "TrendingIcon",
            title: "Fique à frente com as tendências de marketing móvel",
            description:
              "O marketing móvel está inegavelmente na moda. Aproveitar seu potencial para oferecer aos clientes acesso contínuo a informações, descontos exclusivos e promoções o posicionará à frente de seus concorrentes.",
          },
          {
            icon: "BagIcon",
            title:
              "Entenda o comportamento do cliente para um marketing melhor",
            description:
              "Ao integrar soluções de rastreamento como códigos QR, você pode desbloquear informações valiosas sobre as preferências dos clientes, permitindo que você ajuste suas estratégias de marketing para atender às suas necessidades.",
          },
          {
            icon: "PrintIcon",
            title: "Expanda seu alcance com mídia impressa",
            description:
              "Incorpore códigos QR em seus materiais impressos (brochuras, folhetos e cartões de visita) para fornecer aos clientes acesso imediato ao seu conteúdo digital e promoções, preenchendo a lacuna entre experiências offline e online.",
          },
          {
            icon: "CardIcon",
            title:
              "Economize tempo e dinheiro com ferramentas de marketing fáceis",
            description:
              "Os códigos QR são uma ferramenta de marketing acessível e fácil de gerar que pode ser produzida e distribuída rapidamente, garantindo economias significativas em tempo e orçamento para suas campanhas promocionais.",
          },
          {
            icon: "ConversionIcon",
            title:
              "Aumente a eficácia do marketing por meio de jornadas do cliente otimizadas",
            description:
              "Os códigos QR simplificam a experiência do cliente, tornando mais fácil atrair e reter seu público. Ao oferecer acesso direto a promoções e informações, você aumenta significativamente a eficiência de suas táticas de marketing.",
          },
          {
            icon: "BrandingIcon",
            title:
              "Eleve o reconhecimento da marca com códigos QR personalizados",
            description:
              "Os códigos QR podem ser personalizados com o logotipo e o esquema de cores exclusivos de sua marca, fortalecendo sua identidade de marca e promovendo maior conscientização entre seu público-alvo.",
          },
        ],
      },
      {
        locale: "ja",
        title: "QRコードマーケティングが賢明な選択である理由",
        data: [
          {
            icon: "TrendingIcon",
            title: "モバイルマーケティングのトレンドを先取りする",
            description:
              "モバイルマーケティングが流行しているのは間違いありません。顧客に情報、限定割引、プロモーションへのシームレスなアクセスを提供する可能性を活用することで、競合他社に先んじることができます。",
          },
          {
            icon: "BagIcon",
            title: "より良いマーケティングのために顧客行動を理解する",
            description:
              "QRコードのような追跡ソリューションを統合することで、顧客の好みに関する貴重な洞察を得ることができ、顧客のニーズに合わせてマーケティング戦略を微調整することができます。",
          },
          {
            icon: "PrintIcon",
            title: "印刷メディアでリーチを拡大する",
            description:
              "パンフレット、チラシ、名刺などの印刷物資産にQRコードを組み込み、顧客がデジタルコンテンツやプロモーションにすぐにアクセスできるようにし、オフラインエクスペリエンスとオンラインエクスペリエンスの間のギャップを埋めます。",
          },
          {
            icon: "CardIcon",
            title: "手間のかからないマーケティングツールで時間とお金を節約",
            description:
              "QRコードは手頃な価格で簡単に生成できるマーケティングツールであり、迅速に作成および配布できるため、プロモーションキャンペーンの時間と予算の両方を大幅に節約できます。",
          },
          {
            icon: "ConversionIcon",
            title:
              "合理化された顧客ジャーニーを通じてマーケティング効果を高める",
            description:
              "QRコードは顧客体験を簡素化し、視聴者を引き付け、維持しやすくします。プロモーションや情報への直接アクセスを提供することで、マーケティング戦術の効率を大幅に向上させます。",
          },
          {
            icon: "BrandingIcon",
            title: "パーソナライズされたQRコードでブランド認知度を高める",
            description:
              "QRコードは、独自のブランドロゴとカラースキームでカスタマイズでき、ブランドアイデンティティを強化し、ターゲット層の間でより大きな認知度を促進します。",
          },
        ],
      },
      {
        locale: "zh",
        title: "为什么选择二维码营销是一个明智的选择",
        data: [
          {
            icon: "TrendingIcon",
            title: "紧跟移动营销趋势",
            description:
              "毫无疑问，移动营销正风靡一时。利用其潜力为客户提供无缝的信息访问、独家折扣和促销活动，将使您在竞争对手中脱颖而出。",
          },
          {
            icon: "BagIcon",
            title: "了解客户行为，优化营销策略",
            description:
              "通过集成二维码等跟踪解决方案，您可以深入了解客户偏好，从而微调您的营销策略以满足他们的需求。",
          },
          {
            icon: "PrintIcon",
            title: "通过印刷媒体扩大您的影响力",
            description:
              "将二维码整合到您的印刷资产（宣传册、传单和名片）中，为客户提供直接访问您的数字内容和促销活动的机会，从而弥合线下和线上体验之间的差距。",
          },
          {
            icon: "CardIcon",
            title: "使用便捷的营销工具节省时间和金钱",
            description:
              "二维码是一种经济实惠、易于生成的营销工具，可以快速制作和分发，确保您的促销活动在时间和预算方面都大大节省。",
          },
          {
            icon: "ConversionIcon",
            title: "通过简化的客户旅程提高营销效率",
            description:
              "二维码简化了客户体验，使您更容易吸引和留住受众。通过提供对促销活动和信息的直接访问，您将显著提高营销策略的效率。",
          },
          {
            icon: "BrandingIcon",
            title: "使用个性化二维码提升品牌认知度",
            description:
              "二维码可以使用您独特的品牌标志和配色方案进行定制，从而加强您的品牌形象，并提高目标受众的认知度。",
          },
        ],
      },
      {
        locale: "vi",
        title: "Tại sao Marketing bằng mã QR là một lựa chọn thông minh",
        data: [
          {
            icon: "TrendingIcon",
            title: "Đón đầu xu hướng Marketing trên thiết bị di động",
            description:
              "Marketing trên thiết bị di động đang là xu hướng không thể phủ nhận. Khai thác tiềm năng của nó để cung cấp cho khách hàng quyền truy cập liền mạch vào thông tin, giảm giá độc quyền và khuyến mãi sẽ giúp bạn vượt lên trên các đối thủ cạnh tranh.",
          },
          {
            icon: "BagIcon",
            title: "Hiểu hành vi của khách hàng để Marketing tốt hơn",
            description:
              "Bằng cách tích hợp các giải pháp theo dõi như mã QR, bạn có thể mở khóa những hiểu biết có giá trị về sở thích của khách hàng, cho phép bạn tinh chỉnh các chiến lược marketing của mình để đáp ứng nhu cầu của họ.",
          },
          {
            icon: "PrintIcon",
            title: "Mở rộng phạm vi tiếp cận của bạn với các phương tiện in ấn",
            description:
              "Kết hợp mã QR vào các tài sản in ấn của bạn—tờ rơi, tờ quảng cáo và danh thiếp—để cung cấp cho khách hàng quyền truy cập ngay lập tức vào nội dung kỹ thuật số và các chương trình khuyến mãi của bạn, thu hẹp khoảng cách giữa trải nghiệm ngoại tuyến và trực tuyến.",
          },
          {
            icon: "CardIcon",
            title:
              "Tiết kiệm thời gian và tiền bạc với các công cụ Marketing dễ dàng",
            description:
              "Mã QR là một công cụ marketing có giá cả phải chăng, dễ tạo ra có thể được sản xuất và phân phối nhanh chóng, đảm bảo tiết kiệm đáng kể cả về thời gian và ngân sách cho các chiến dịch quảng cáo của bạn.",
          },
          {
            icon: "ConversionIcon",
            title:
              "Nâng cao hiệu quả Marketing thông qua hành trình khách hàng được sắp xếp hợp lý",
            description:
              "Mã QR đơn giản hóa trải nghiệm của khách hàng, giúp bạn dễ dàng thu hút và giữ chân khán giả của mình. Bằng cách cung cấp quyền truy cập trực tiếp vào các chương trình khuyến mãi và thông tin, bạn sẽ tăng đáng kể hiệu quả của các chiến thuật marketing của mình.",
          },
          {
            icon: "BrandingIcon",
            title: "Nâng cao nhận diện thương hiệu bằng mã QR được cá nhân hóa",
            description:
              "Mã QR có thể được tùy chỉnh bằng logo thương hiệu độc đáo và bảng màu của bạn, tăng cường nhận diện thương hiệu và thúc đẩy nhận thức lớn hơn giữa nhóm nhân khẩu học mục tiêu của bạn.",
          },
        ],
      },
      {
        locale: "id",
        title: "Mengapa Pemasaran Kode QR Adalah Pilihan Cerdas",
        data: [
          {
            icon: "TrendingIcon",
            title: "Tetap Terdepan dengan Tren Pemasaran Seluler",
            description:
              "Pemasaran seluler tidak dapat disangkal sedang populer. Memanfaatkan potensinya untuk menawarkan pelanggan akses tanpa hambatan ke informasi, diskon eksklusif, dan promosi akan menempatkan Anda di depan pesaing Anda.",
          },
          {
            icon: "BagIcon",
            title: "Pahami Perilaku Pelanggan untuk Pemasaran yang Lebih Baik",
            description:
              "Dengan mengintegrasikan solusi pelacakan seperti kode QR, Anda dapat membuka wawasan berharga tentang preferensi pelanggan, memungkinkan Anda untuk menyempurnakan strategi pemasaran Anda untuk memenuhi kebutuhan mereka.",
          },
          {
            icon: "PrintIcon",
            title: "Perluas Jangkauan Anda dengan Media Cetak",
            description:
              "Sertakan kode QR ke dalam aset cetak Anda—brosur, flyer, dan kartu nama—untuk memberi pelanggan akses langsung ke konten digital dan promosi Anda, menjembatani kesenjangan antara pengalaman offline dan online.",
          },
          {
            icon: "CardIcon",
            title: "Hemat Waktu dan Uang dengan Alat Pemasaran yang Mudah",
            description:
              "Kode QR adalah alat pemasaran yang terjangkau dan mudah dibuat yang dapat diproduksi dan didistribusikan dengan cepat, memastikan penghematan yang signifikan dalam waktu dan anggaran untuk kampanye promosi Anda.",
          },
          {
            icon: "ConversionIcon",
            title:
              "Tingkatkan Efektivitas Pemasaran melalui Perjalanan Pelanggan yang Efisien",
            description:
              "Kode QR menyederhanakan pengalaman pelanggan, sehingga lebih mudah untuk menarik dan mempertahankan audiens Anda. Dengan menawarkan akses langsung ke promosi dan informasi, Anda secara signifikan meningkatkan efisiensi taktik pemasaran Anda.",
          },
          {
            icon: "BrandingIcon",
            title:
              "Tingkatkan Pengenalan Merek dengan Kode QR yang Dipersonalisasi",
            description:
              "Kode QR dapat disesuaikan dengan logo merek dan skema warna unik Anda, memperkuat identitas merek Anda dan menumbuhkan kesadaran yang lebih besar di antara demografi target Anda.",
          },
        ],
      },
      {
        locale: "tr",
        title: "Neden QR Kod Pazarlaması Akıllıca Bir Seçimdir",
        data: [
          {
            icon: "TrendingIcon",
            title: "Mobil Pazarlama Trendleriyle Önde Kalın",
            description:
              "Mobil pazarlama yadsınamaz bir şekilde moda. Müşterilere bilgiye, özel indirimlere ve promosyonlara sorunsuz erişim sunma potansiyelinden yararlanmak, sizi rakiplerinizin önüne geçirecektir.",
          },
          {
            icon: "BagIcon",
            title: "Daha İyi Pazarlama için Müşteri Davranışlarını Anlayın",
            description:
              "QR kodları gibi izleme çözümlerini entegre ederek, müşteri tercihleri hakkında değerli bilgiler edinebilir ve pazarlama stratejilerinizi ihtiyaçlarını karşılayacak şekilde ince ayarlamanızı sağlayabilirsiniz.",
          },
          {
            icon: "PrintIcon",
            title: "Basılı Medya ile Erişim Alanınızı Genişletin",
            description:
              "Müşterilere dijital içeriğinize ve promosyonlarınıza anında erişim sağlamak, çevrimiçi ve çevrimdışı deneyimler arasındaki boşluğu kapatmak için QR kodlarını basılı varlıklarınıza—broşürlerinize, el ilanlarınıza ve kartvizitlerinize—ekleyin.",
          },
          {
            icon: "CardIcon",
            title:
              "Zahmetsiz Pazarlama Araçlarıyla Zamandan ve Paradan Tasarruf Edin",
            description:
              "QR kodları, promosyon kampanyalarınız için hem zamandan hem de bütçeden önemli tasarruflar sağlayarak hızlı bir şekilde üretilebilen ve dağıtılabilen uygun fiyatlı, kolayca oluşturulabilen bir pazarlama aracıdır.",
          },
          {
            icon: "ConversionIcon",
            title:
              "Kolaylaştırılmış Müşteri Yolculukları Aracılığıyla Pazarlama Etkinliğini Artırın",
            description:
              "QR kodları müşteri deneyimini basitleştirerek hedef kitlenizi çekmeyi ve elde tutmayı kolaylaştırır. Promosyonlara ve bilgilere doğrudan erişim sunarak, pazarlama taktiklerinizin verimliliğini önemli ölçüde artırırsınız.",
          },
          {
            icon: "BrandingIcon",
            title:
              "Kişiselleştirilmiş QR Kodlarıyla Marka Tanınırlığını Yükseltin",
            description:
              "QR kodları, benzersiz marka logonuz ve renk şemanızla özelleştirilebilir, bu da marka kimliğinizi güçlendirir ve hedef demografiniz arasında daha fazla farkındalık yaratır.",
          },
        ],
      },
      {
        locale: "hi",
        title: "क्यूआर कोड मार्केटिंग एक स्मार्ट विकल्प क्यों है",
        data: [
          {
            icon: "TrendingIcon",
            title: "मोबाइल मार्केटिंग ट्रेंड के साथ आगे रहें",
            description:
              "मोबाइल मार्केटिंग निस्संदेह चलन में है। ग्राहकों को जानकारी, विशेष छूट और प्रचार तक निर्बाध पहुंच प्रदान करने की अपनी क्षमता का उपयोग करना आपको अपने प्रतिस्पर्धियों से आगे रखेगा।",
          },
          {
            icon: "BagIcon",
            title: "बेहतर मार्केटिंग के लिए ग्राहक व्यवहार को समझें",
            description:
              "क्यूआर कोड जैसे ट्रैकिंग समाधानों को एकीकृत करके, आप ग्राहक प्राथमिकताओं में मूल्यवान अंतर्दृष्टि अनलॉक कर सकते हैं, जिससे आप अपनी मार्केटिंग रणनीतियों को उनकी आवश्यकताओं को पूरा करने के लिए ठीक कर सकते हैं।",
          },
          {
            icon: "PrintIcon",
            title: "मुद्रित मीडिया के साथ अपनी पहुंच का विस्तार करें",
            description:
              "अपने ग्राहकों को आपकी डिजिटल सामग्री और प्रचारों तक तत्काल पहुंच प्रदान करने के लिए, ऑफ़लाइन और ऑनलाइन अनुभवों के बीच के अंतर को कम करने के लिए, अपनी प्रिंट परिसंपत्तियों—पुस्तिकाओं, फ़्लायर्स और व्यवसाय कार्डों—में क्यूआर कोड शामिल करें।",
          },
          {
            icon: "CardIcon",
            title: "सहज विपणन उपकरणों के साथ समय और पैसा बचाएं",
            description:
              "क्यूआर कोड एक किफायती, आसानी से उत्पन्न होने वाला विपणन उपकरण है जिसे जल्दी से निर्मित और वितरित किया जा सकता है, जिससे आपके प्रचार अभियानों के लिए समय और बजट दोनों में महत्वपूर्ण बचत सुनिश्चित होती है।",
          },
          {
            icon: "ConversionIcon",
            title:
              "सुव्यवस्थित ग्राहक यात्राओं के माध्यम से विपणन प्रभावशीलता बढ़ाएँ",
            description:
              "क्यूआर कोड ग्राहक अनुभव को सरल बनाते हैं, जिससे आपके दर्शकों को आकर्षित करना और बनाए रखना आसान हो जाता है। प्रचार और सूचना तक सीधी पहुंच प्रदान करके, आप अपनी विपणन युक्तियों की दक्षता में काफी वृद्धि करते हैं।",
          },
          {
            icon: "BrandingIcon",
            title: "व्यक्तिगत क्यूआर कोड के साथ ब्रांड पहचान बढ़ाएँ",
            description:
              "क्यूआर कोड को आपके अद्वितीय ब्रांड लोगो और रंग योजना के साथ तैयार किया जा सकता है, आपकी ब्रांड पहचान को मजबूत किया जा सकता है और आपके लक्षित जनसांख्यिकी के बीच अधिक जागरूकता को बढ़ावा दिया जा सकता है।",
          },
        ],
      },
      {
        locale: "th",
        title: "เหตุใดการตลาดด้วยรหัส QR จึงเป็นทางเลือกที่ชาญฉลาด",
        data: [
          {
            icon: "TrendingIcon",
            title: "ก้าวนำด้วยเทรนด์การตลาดบนมือถือ",
            description:
              "การตลาดบนมือถือเป็นที่นิยมอย่างปฏิเสธไม่ได้ การใช้ประโยชน์จากศักยภาพเพื่อมอบการเข้าถึงข้อมูล ส่วนลดพิเศษ และโปรโมชั่นให้กับลูกค้าอย่างราบรื่น จะทำให้คุณอยู่เหนือคู่แข่ง",
          },
          {
            icon: "BagIcon",
            title: "ทำความเข้าใจพฤติกรรมลูกค้าเพื่อการตลาดที่ดีขึ้น",
            description:
              "ด้วยการผสานรวมโซลูชันการติดตาม เช่น รหัส QR คุณสามารถปลดล็อกข้อมูลเชิงลึกที่มีค่าเกี่ยวกับความชอบของลูกค้า ทำให้คุณสามารถปรับแต่งกลยุทธ์ทางการตลาดให้ตรงกับความต้องการของพวกเขา",
          },
          {
            icon: "PrintIcon",
            title: "ขยายการเข้าถึงของคุณด้วยสื่อสิ่งพิมพ์",
            description:
              "ใส่รหัส QR ลงในสื่อสิ่งพิมพ์ของคุณ ไม่ว่าจะเป็นโบรชัวร์ ใบปลิว และนามบัตร เพื่อให้ลูกค้าเข้าถึงเนื้อหาดิจิทัลและโปรโมชั่นของคุณได้ทันที เชื่อมช่องว่างระหว่างประสบการณ์ออฟไลน์และออนไลน์",
          },
          {
            icon: "CardIcon",
            title:
              "ประหยัดเวลาและเงินด้วยเครื่องมือการตลาดที่ไม่ต้องใช้ความพยายาม",
            description:
              "รหัส QR เป็นเครื่องมือทางการตลาดที่ราคาไม่แพง สร้างได้ง่าย ซึ่งสามารถผลิตและเผยแพร่ได้อย่างรวดเร็ว ทำให้มั่นใจได้ว่าจะประหยัดเวลาและงบประมาณสำหรับแคมเปญส่งเสริมการขายของคุณได้อย่างมาก",
          },
          {
            icon: "ConversionIcon",
            title: "เพิ่มประสิทธิภาพทางการตลาดผ่านเส้นทางลูกค้าที่คล่องตัว",
            description:
              "รหัส QR ช่วยลดความซับซ้อนของประสบการณ์ของลูกค้า ทำให้ง่ายต่อการดึงดูดและรักษาผู้ชมของคุณ ด้วยการให้การเข้าถึงโปรโมชั่นและข้อมูลโดยตรง คุณจะเพิ่มประสิทธิภาพของกลยุทธ์ทางการตลาดของคุณได้อย่างมาก",
          },
          {
            icon: "BrandingIcon",
            title: "ยกระดับการจดจำแบรนด์ด้วยรหัส QR ส่วนบุคคล",
            description:
              "รหัส QR สามารถปรับแต่งด้วยโลโก้แบรนด์และโทนสีที่เป็นเอกลักษณ์ของคุณ ซึ่งจะช่วยเสริมสร้างเอกลักษณ์ของแบรนด์และส่งเสริมการรับรู้ที่มากขึ้นในกลุ่มเป้าหมายของคุณ",
          },
        ],
      },
    ]);
    const { locale } = useI18n();
    return {
      dataLocale,
      locale,
    };
  },
});
</script>
